/**
 * React Query hooks for the staff side of the Service Desk (queue, ticket
 * workspace, settings, knowledge base). The user-facing calls live in
 * `serviceDeskClient.ts`. Contract: `docs/servicedesk-api.md` in the api repo.
 *
 * Configuration routes return the full list after a write; these hooks simply
 * invalidate the matching read, which keeps them correct if a response shape
 * differs a little from what the types say.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { SdFormField, SdPerson } from '@ampersandbrands/ui';
import { apiDelete, apiPatch, apiPost, apiPut, apiGet } from './api';
import { fileToBase64 } from './serviceDeskFiles';
import {
  SD_DESK,
  SD_BASE,
  sdAttachmentUrl,
  sdDeskBase,
  sdGet,
  sdHtml,
  sdPost,
  sdQuery,
} from './serviceDeskClient';
import type {
  SdArticleInput,
  SdApproverLists,
  SdPilotStore,
  SdAutomation,
  SdAutomationRun,
  SdCategoryAdminNode,
  SdCategoryInput,
  SdDeskConfig,
  SdDeskSummary,
  SdFieldAdmin,
  SdFieldInput,
  SdQueueFilters,
  SdQueuePage,
  SdPersonContact,
  SdSlaRow,
  SdStaffArticle,
  SdStaffArticleRow,
  SdStaffCommentInput,
  SdStaffTicketDetail,
  SdStaffTicketRow,
  SdStats,
  SdTicketPatch,
  SdTopic,
} from './serviceDeskTypes';

export const hasServiceDeskAccess = (perms: string[] | undefined, superAdmin = false): boolean =>
  superAdmin || !!perms?.includes('it.servicedesk');

const K = (...parts: unknown[]) => ['servicedesk', SD_DESK, ...parts] as const;

type Envelope<T> = { data: T };
async function sdSend<T>(method: 'PATCH' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const res =
    method === 'PATCH'
      ? await apiPatch<Envelope<T>>(path, body)
      : method === 'PUT'
        ? await apiPut<Envelope<T>>(path, body)
        : await apiDelete<Envelope<T>>(path);
  return res?.data;
}

// --- Queue and ticket ------------------------------------------------------

export const QUEUE_PAGE_SIZE = 50;

export function useQueue(filters: SdQueueFilters) {
  return useInfiniteQuery<SdQueuePage, Error>({
    queryKey: K('queue', filters),
    initialPageParam: '',
    queryFn: async ({ pageParam }) => {
      const qs = sdQuery({
        status: filters.status.join(','),
        priority: filters.priority.join(','),
        assignee: filters.assignee,
        category_id: filters.category_id,
        store_no: filters.store_no,
        q: filters.q.trim(),
        sla: filters.sla,
        sort: filters.sort,
        limit: QUEUE_PAGE_SIZE,
        cursor: pageParam as string,
      });
      const res = await apiGet<{ data: SdStaffTicketRow[]; next_cursor?: string | null }>(
        `${sdDeskBase()}/tickets${qs}`,
      );
      return { rows: res.data ?? [], next_cursor: res.next_cursor ?? null };
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: (prev) => prev,
    staleTime: 15_000,
  });
}

export function useStats() {
  return useQuery<SdStats, Error>({
    queryKey: K('stats'),
    queryFn: () => sdGet<SdStats>(`${sdDeskBase()}/stats`),
    refetchInterval: 60_000,
  });
}

export function useTicket(id: number) {
  return useQuery<SdStaffTicketDetail, Error>({
    queryKey: K('ticket', id),
    queryFn: async () => {
      const t = await sdGet<SdStaffTicketDetail>(`${sdDeskBase()}/tickets/${id}`);
      return {
        ...t,
        attachments: (t.attachments ?? []).map((a) => ({ ...a, url: sdAttachmentUrl(a.url) })),
        description_html: t.description_html ? sdHtml(t.description_html) : t.description_html,
        comments: t.comments.map((c) => ({
          ...c,
          body_html: sdHtml(c.body_html),
          attachments: (c.attachments ?? []).map((a) => ({ ...a, url: sdAttachmentUrl(a.url) })),
        })),
      };
    },
    enabled: Number.isFinite(id),
  });
}

function useTicketRefresh(id: number) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: K('ticket', id) });
    void qc.invalidateQueries({ queryKey: K('queue') });
    void qc.invalidateQueries({ queryKey: K('stats') });
  };
}

export function usePatchTicket(id: number) {
  const refresh = useTicketRefresh(id);
  return useMutation<unknown, Error, SdTicketPatch>({
    mutationFn: (patch) => sdSend('PATCH', `${sdDeskBase()}/tickets/${id}`, patch),
    onSuccess: refresh,
  });
}

export function useAddComment(id: number) {
  const refresh = useTicketRefresh(id);
  return useMutation<unknown, Error, SdStaffCommentInput>({
    mutationFn: (input) => sdPost(`${sdDeskBase()}/tickets/${id}/comments`, input),
    onSuccess: refresh,
  });
}

/** An HR approver approves or declines an Employee exit ticket from the ticket page. */
export function useDecideApproval(id: number) {
  const refresh = useTicketRefresh(id);
  return useMutation<unknown, Error, { decision: 'approve' | 'decline'; reason?: string }>({
    mutationFn: (input) => sdPost(`${SD_BASE}/tickets/${id}/approval`, input),
    onSuccess: refresh,
    // A refused decision (someone else decided first) still changes what the page should show.
    onError: refresh,
  });
}

export function useMoveTicket(id: number) {
  const refresh = useTicketRefresh(id);
  return useMutation<unknown, Error, string>({
    mutationFn: (toDesk) => sdPost(`${sdDeskBase()}/tickets/${id}/move`, { to_desk: toDesk }),
    onSuccess: refresh,
  });
}

export function useConfirmBug(id: number) {
  const refresh = useTicketRefresh(id);
  return useMutation<unknown, Error, void>({
    mutationFn: () => sdPost(`${sdDeskBase()}/tickets/${id}/confirm-bug`, {}),
    onSuccess: refresh,
  });
}

/** Desks the caller may work (for "Move to desk"). */
export function useDesks() {
  return useQuery<SdDeskSummary[], Error>({
    queryKey: ['servicedesk', 'desks'],
    queryFn: () => sdGet<SdDeskSummary[]>(`${SD_BASE}/desks`),
    staleTime: 5 * 60_000,
  });
}

/** Directory search for assignee and @mention pickers. */
export function usePeopleSearch(q: string, enabled = true) {
  const term = q.trim();
  return useQuery<SdPerson[], Error>({
    queryKey: ['servicedesk', 'people', term],
    queryFn: () => sdGet<SdPerson[]>(`${SD_BASE}/people${sdQuery({ scope: 'all', q: term })}`),
    enabled: enabled && term.length >= 2,
    staleTime: 60_000,
  });
}

/**
 * Contact details for a person, for the reporter hover card. One request per
 * email while the page stays open; a 404 or a failure is cached as "unavailable"
 * rather than retried on every hover.
 */
export function usePersonContact(email: string | null | undefined, enabled = true) {
  return useQuery<SdPersonContact | null, Error>({
    queryKey: ['servicedesk', 'contact', (email ?? '').toLowerCase()],
    enabled: enabled && !!email,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: false,
    queryFn: async () => {
      const res = await apiGet<SdPersonContact | { data: SdPersonContact | null }>(
        `${SD_BASE}/people/contact${sdQuery({ email })}`,
      );
      if (res && typeof res === 'object' && 'data' in res) return res.data ?? null;
      return (res as SdPersonContact) ?? null;
    },
  });
}

export function useStoresList() {
  return useQuery<{ store_no: string; name: string }[], Error>({
    queryKey: ['servicedesk', 'stores'],
    queryFn: () => sdGet(`${SD_BASE}/stores`),
    staleTime: 10 * 60_000,
  });
}

// --- Configuration ---------------------------------------------------------

export function useDeskConfig() {
  return useQuery<SdDeskConfig, Error>({
    queryKey: K('config'),
    queryFn: () => sdGet<SdDeskConfig>(sdDeskBase()),
  });
}

export function usePatchDeskConfig() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, Partial<SdDeskConfig>>({
    mutationFn: (patch) => sdSend('PATCH', sdDeskBase(), patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: K('config') }),
  });
}

export function useCategoriesAll() {
  return useQuery<SdCategoryAdminNode[], Error>({
    queryKey: K('categories-all'),
    queryFn: () => sdGet<SdCategoryAdminNode[]>(`${sdDeskBase()}/categories/all`),
  });
}

function useCategoryRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: K('categories-all') });
    void qc.invalidateQueries({ queryKey: K('fields') });
  };
}

export function useCreateCategory() {
  const refresh = useCategoryRefresh();
  return useMutation<unknown, Error, SdCategoryInput>({
    mutationFn: (input) => sdPost(`${sdDeskBase()}/categories`, input),
    onSuccess: refresh,
  });
}

export function useUpdateCategory() {
  const refresh = useCategoryRefresh();
  return useMutation<unknown, Error, { id: number; patch: Partial<SdCategoryInput> }>({
    mutationFn: ({ id, patch }) => sdSend('PATCH', `${sdDeskBase()}/categories/${id}`, patch),
    onSuccess: refresh,
  });
}

/** Soft delete (deactivate). The api refuses when active children exist. */
export function useDeleteCategory() {
  const refresh = useCategoryRefresh();
  return useMutation<unknown, Error, number>({
    mutationFn: (id) => sdSend('DELETE', `${sdDeskBase()}/categories/${id}`),
    onSuccess: refresh,
  });
}

/** Fields defined ON a category (not inherited). `null` = desk-wide fields. */
export function useFields(categoryId: number | null) {
  return useQuery<SdFieldAdmin[], Error>({
    queryKey: K('fields', categoryId),
    queryFn: () => sdGet<SdFieldAdmin[]>(`${sdDeskBase()}/fields${sdQuery({ category_id: categoryId })}`),
  });
}

/** Fields a ticket form shows for a category: desk-wide, then inherited root to leaf. Used to pick 'show if' sources. */
export function useFormFields(categoryId: number | null) {
  return useQuery<SdFormField[], Error>({
    queryKey: K('fields', 'form', categoryId),
    queryFn: () => sdGet<SdFormField[]>(`${sdDeskBase()}/form${sdQuery({ category_id: categoryId })}`),
  });
}

function useFieldRefresh() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: K('fields') });
}

export function useCreateField() {
  const refresh = useFieldRefresh();
  return useMutation<unknown, Error, SdFieldInput>({
    mutationFn: (input) => sdPost(`${sdDeskBase()}/fields`, input),
    onSuccess: refresh,
  });
}

export function useUpdateField() {
  const refresh = useFieldRefresh();
  return useMutation<unknown, Error, { id: number; patch: Partial<SdFieldInput> }>({
    mutationFn: ({ id, patch }) => sdSend('PATCH', `${sdDeskBase()}/fields/${id}`, patch),
    onSuccess: refresh,
  });
}

export function useDeleteField() {
  const refresh = useFieldRefresh();
  return useMutation<unknown, Error, number>({
    mutationFn: (id) => sdSend('DELETE', `${sdDeskBase()}/fields/${id}`),
    onSuccess: refresh,
  });
}

export function useSla() {
  return useQuery<SdSlaRow[], Error>({
    queryKey: K('sla'),
    queryFn: () => sdGet<SdSlaRow[]>(`${sdDeskBase()}/sla`),
  });
}

export function usePutSla() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, SdSlaRow[]>({
    mutationFn: (rows) => sdSend('PUT', `${sdDeskBase()}/sla`, { rows }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: K('sla') }),
  });
}

export function useTopics() {
  return useQuery<SdTopic[], Error>({
    queryKey: K('topics'),
    queryFn: () => sdGet<SdTopic[]>(`${sdDeskBase()}/topics`),
    staleTime: 60_000,
  });
}

export function useCreateTopic() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { name: string }>({
    mutationFn: (input) => sdPost(`${sdDeskBase()}/topics`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: K('topics') }),
  });
}

export function useUpdateTopic() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: number; patch: Partial<Pick<SdTopic, 'name' | 'is_active'>> }>({
    mutationFn: ({ id, patch }) => sdSend('PATCH', `${sdDeskBase()}/topics/${id}`, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: K('topics') }),
  });
}

// --- HR approvers ----------------------------------------------------------

export function useApprovers() {
  return useQuery<SdApproverLists, Error>({
    queryKey: K('approvers'),
    queryFn: () => sdGet<SdApproverLists>(`${sdDeskBase()}/approvers`),
  });
}

/** Replace one approver list (`hr_exit`) with these addresses. */
export function usePutApprovers() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { purpose: string; emails: string[] }>({
    mutationFn: ({ purpose, emails }) => sdSend('PUT', `${sdDeskBase()}/approvers/${encodeURIComponent(purpose)}`, { emails }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: K('approvers') }),
  });
}

// --- Sweet Spot pilot stores -----------------------------------------------

/** Stores that get the new Help button in The Sweet Spot. Every other store keeps the old help. */
export function usePilotStores() {
  return useQuery<SdPilotStore[], Error>({
    queryKey: K('sweetspot-pilot'),
    queryFn: () => sdGet<SdPilotStore[]>(`${sdDeskBase()}/sweetspot-pilot`),
  });
}

/** Replace the pilot list with these store numbers. */
export function usePutPilotStores() {
  const qc = useQueryClient();
  return useMutation<SdPilotStore[], Error, string[]>({
    mutationFn: (storeNos) => sdSend<SdPilotStore[]>('PUT', `${sdDeskBase()}/sweetspot-pilot`, { store_nos: storeNos }),
    onSuccess: (list) => qc.setQueryData(K('sweetspot-pilot'), list),
  });
}

export function useAutomations() {
  return useQuery<SdAutomation[], Error>({
    queryKey: K('automations'),
    queryFn: () => sdGet<SdAutomation[]>(`${sdDeskBase()}/automations`),
  });
}

export function usePatchAutomation() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: number; patch: { enabled?: boolean; config?: Record<string, unknown> } }>({
    mutationFn: ({ id, patch }) => sdSend('PATCH', `${sdDeskBase()}/automations/${id}`, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: K('automations') }),
  });
}

export function useAutomationRuns(id: number, enabled: boolean) {
  return useQuery<SdAutomationRun[], Error>({
    queryKey: K('automation-runs', id),
    queryFn: () => sdGet<SdAutomationRun[]>(`${sdDeskBase()}/automations/${id}/runs${sdQuery({ limit: 25 })}`),
    enabled,
  });
}

// --- Knowledge base --------------------------------------------------------

export function useArticlesStaff(filters: { status: string; q: string }) {
  return useQuery<SdStaffArticleRow[], Error>({
    queryKey: K('articles', filters),
    queryFn: () =>
      sdGet<SdStaffArticleRow[]>(
        `${sdDeskBase()}/articles${sdQuery({ status: filters.status, q: filters.q.trim() })}`,
      ),
    placeholderData: (prev) => prev,
  });
}

export function useArticleStaff(id: number | null) {
  return useQuery<SdStaffArticle, Error>({
    queryKey: K('article', id),
    queryFn: () => sdGet<SdStaffArticle>(`${sdDeskBase()}/articles/${id}`),
    enabled: id !== null && Number.isFinite(id),
  });
}

function useArticleRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: K('articles') });
    void qc.invalidateQueries({ queryKey: K('article') });
  };
}

export function useCreateArticle() {
  const refresh = useArticleRefresh();
  return useMutation<SdStaffArticle, Error, SdArticleInput>({
    mutationFn: (input) => sdPost<SdStaffArticle>(`${sdDeskBase()}/articles`, input),
    onSuccess: refresh,
  });
}

export function usePatchArticle(id: number) {
  const refresh = useArticleRefresh();
  return useMutation<unknown, Error, Partial<SdArticleInput>>({
    mutationFn: (patch) => sdSend('PATCH', `${sdDeskBase()}/articles/${id}`, patch),
    onSuccess: refresh,
  });
}

export function usePublishArticle(id: number) {
  const refresh = useArticleRefresh();
  return useMutation<unknown, Error, void>({
    mutationFn: () => sdPost(`${sdDeskBase()}/articles/${id}/publish`, {}),
    onSuccess: refresh,
  });
}

export function useRetireArticle(id: number) {
  const refresh = useArticleRefresh();
  return useMutation<unknown, Error, void>({
    mutationFn: () => sdPost(`${sdDeskBase()}/articles/${id}/retire`, {}),
    onSuccess: refresh,
  });
}

/** Image upload for the article editor. Returns a src the editor can embed right away. */
export function articleImageUploader(articleId: number) {
  return async (file: File): Promise<{ src: string }> => {
    const res = await apiPost<Envelope<{ url: string }>>(`${sdDeskBase()}/articles/${articleId}/assets`, {
      filename: file.name || 'pasted-image.png',
      content_type: file.type || 'image/png',
      base64: await fileToBase64(file),
    });
    return { src: sdAttachmentUrl(res.data.url) };
  };
}
